import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserRound, Lock, Eye, EyeOff, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { accountProfile, accountUpdate, accountChangePassword, accountDelete, getCountries, errorMessage } from "./api";
import { card, field } from "./ui";

// "My profile": the signed-in person's own details, with the ability to update them and to change their password.
// Which fields can be changed is decided by the server (the `editable` flags); this page only mirrors it.

const LANGUAGES = [["en", "English"], ["ar", "العربية (Arabic)"], ["ur", "اردو (Urdu)"], ["ta", "தமிழ் (Tamil)"], ["fr", "Français"], ["ms", "Bahasa Melayu"], ["sw", "Kiswahili"]];
const COMMON_ZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Riyadh", "Asia/Karachi", "Asia/Dhaka", "Asia/Kuala_Lumpur", "Asia/Singapore", "Africa/Nairobi", "Africa/Cairo", "Europe/London", "Europe/Paris", "America/New_York", "America/Chicago", "America/Los_Angeles", "Australia/Sydney", "UTC"];
const ROLE = { individual: "Individual learner", organisation: "Principal", teacher: "Teacher", student: "Student" };

const when = (iso) => { if (!iso) return "—"; const d = new Date(iso); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); };

function Row({ label, children, hint, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-foreground/60 mt-1">{hint}</p>}
    </div>
  );
}

function Details({ token, p, onSaved }) {
  const u = p.user;
  const ed = p.editable;
  const individual = u.account_kind === "individual";
  const zones = useMemo(() => {
    let all = [];
    try { all = Intl.supportedValuesOf("timeZone"); } catch { all = COMMON_ZONES; }
    // the common zones first (so they are always offered under their familiar names), then everything the browser knows
    return [...new Set([...(u.timezone ? [u.timezone] : []), ...COMMON_ZONES, ...all])];
  }, [u.timezone]);
  const [countries, setCountries] = useState([]);
  useEffect(() => { if (individual) getCountries().then(setCountries).catch(() => setCountries([])); }, [individual]);
  const initial = useMemo(
    () => ({ name: u.name || "", phone: u.phone || "", code: u.country_calling_code || "", mobile: u.mobile_number || "", locale: u.locale || "en", timezone: u.timezone || "", username: u.username || "", age: u.age ? String(u.age) : "" }),
    [u.name, u.phone, u.country_calling_code, u.mobile_number, u.locale, u.timezone, u.username, u.age],
  );
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setF(initial); }, [initial]);   // a fresh copy from the server replaces what is in the form
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const codes = [...new Map(countries.map((c) => [c.calling_code, c.name])).entries()];

  const save = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const body = {};
    if (ed.name && f.name.trim() !== (u.name || "")) body.name = f.name.trim();
    if (ed.phone) {
      if (individual) { if (f.code !== (u.country_calling_code || "") || f.mobile !== (u.mobile_number || "")) { body.country_calling_code = f.code; body.mobile_number = f.mobile.trim(); } }
      else if (f.phone !== (u.phone || "")) body.phone = f.phone.trim();
    }
    if (ed.locale && f.locale !== (u.locale || "en")) body.locale = f.locale;
    if (ed.timezone && f.timezone !== (u.timezone || "")) body.timezone = f.timezone;
    if (ed.username && f.username.trim() && f.username.trim() !== (u.username || "")) body.username = f.username.trim();
    if (ed.age && f.age !== (u.age ? String(u.age) : "")) body.age = f.age ? Number(f.age) : null;
    try { await accountUpdate(token, body); toast.success("Your details were updated"); onSaved(); }
    catch (e2) { setErr(errorMessage(e2, "We couldn't save your details")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className={`${card} space-y-4`} data-testid="profile-details">
      <h2 className="font-display font-semibold text-lg text-forest-700">Your details</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Row label="Full name" htmlFor="pf-name" hint={!ed.name ? "Your name is kept by your school. Ask your teacher or principal to change it." : null}>
          <input id="pf-name" className={`${field} ${!ed.name ? "bg-sand-100" : ""}`} value={f.name} maxLength={120} disabled={!ed.name || p.demo} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pf-name" required />
        </Row>
        {u.email !== null && (
          <Row label="E-mail" htmlFor="pf-email" hint="This is how you sign in, so it can't be changed here. Contact support if you need a different one.">
            <div className={`${field} bg-sand-100 inline-flex items-center gap-2`} data-testid="pf-email"><Lock className="h-3.5 w-3.5 text-foreground/50" aria-hidden />{u.email}</div>
          </Row>
        )}
        {ed.username ? (
          <Row label="Username" htmlFor="pf-username-input" hint="Pick one to be found by when a Family-plan owner invites you (they type your username, e-mail and age). You can set it once; it can't be changed afterwards.">
            <input id="pf-username-input" className={field} value={f.username} maxLength={30} autoComplete="off" placeholder="e.g. amina.k" onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, "") })} data-testid="pf-username-input" />
          </Row>
        ) : u.username && (
          <Row label="Username" hint={individual ? "You can also sign in with this. It can't be changed once set." : "You sign in with this. Your school can change it for you."}>
            <div className={`${field} bg-sand-100 inline-flex items-center gap-2`} data-testid="pf-username"><Lock className="h-3.5 w-3.5 text-foreground/50" aria-hidden />{u.username}</div>
          </Row>
        )}
        {ed.age && (
          <Row label="Age" htmlFor="pf-age" hint="Used with your username and e-mail when a Family-plan owner invites you.">
            <input id="pf-age" className={field} inputMode="numeric" value={f.age} maxLength={3} placeholder="Years" onChange={(e) => setF({ ...f, age: e.target.value.replace(/\D/g, "") })} data-testid="pf-age" />
          </Row>
        )}
        {ed.phone && (individual ? (
          <Row label="Mobile number" htmlFor="pf-mobile" hint="Used for WhatsApp messages about your learning.">
            <div className="flex gap-2">
              <select className={`${field} max-w-[120px]`} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} aria-label="Country code" data-testid="pf-code">
                <option value="">Code</option>
                {f.code && !codes.some(([c]) => c === f.code) && <option value={f.code}>{f.code}</option>}
                {codes.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
              </select>
              <input id="pf-mobile" className={field} inputMode="numeric" value={f.mobile} maxLength={15} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "") })} data-testid="pf-mobile" />
            </div>
          </Row>
        ) : (
          <Row label="Contact phone" htmlFor="pf-phone">
            <input id="pf-phone" className={field} value={f.phone} maxLength={25} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="pf-phone" />
          </Row>
        ))}
        <Row label="Language" htmlFor="pf-locale">
          <select id="pf-locale" className={field} value={f.locale} disabled={p.demo} onChange={(e) => setF({ ...f, locale: e.target.value })} data-testid="pf-locale">
            {LANGUAGES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            {!LANGUAGES.some(([c]) => c === f.locale) && <option value={f.locale}>{f.locale}</option>}
          </select>
        </Row>
        <Row label="Time zone" htmlFor="pf-tz" hint={<button type="button" className="underline" onClick={() => setF({ ...f, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "" })}>Use this device's time zone</button>}>
          <select id="pf-tz" className={field} value={f.timezone} disabled={p.demo} onChange={(e) => setF({ ...f, timezone: e.target.value })} data-testid="pf-tz">
            <option value="">Not set</option>
            {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
          </select>
        </Row>
      </div>
      {err && <p className="text-sm text-red-700" role="alert" data-testid="pf-error">{err}</p>}
      {p.demo && <p className="text-sm text-amber-800 bg-gold-500/10 rounded-xl px-3 py-2">This is a shared demo account, so its details can't be changed.</p>}
      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary disabled:opacity-50" disabled={busy || !dirty || p.demo} data-testid="pf-save">{busy ? "Saving…" : "Save changes"}</button>
        {dirty && !busy && <button type="button" className="btn-ghost" onClick={() => setF(initial)}>Discard</button>}
      </div>
    </form>
  );
}

function Password({ token, p }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const min = p.password_min_length || 8;
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (next.length < min) { setErr(`Use at least ${min} characters.`); return; }
    if (next !== again) { setErr("The two new passwords don't match."); return; }
    setBusy(true);
    try { await accountChangePassword(token, { ...(p.has_password ? { current_password: cur } : {}), new_password: next }); toast.success("Your password was changed"); setCur(""); setNext(""); setAgain(""); }
    catch (e2) { setErr(errorMessage(e2, "We couldn't change your password")); }
    finally { setBusy(false); }
  };
  const type = show ? "text" : "password";
  return (
    <form onSubmit={submit} className={`${card} space-y-4`} data-testid="profile-password">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display font-semibold text-lg text-forest-700 inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5" aria-hidden />Password</h2>
        <button type="button" className="text-sm text-foreground/70 inline-flex items-center gap-1" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide passwords" : "Show passwords"} data-testid="pw-toggle">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{show ? "Hide" : "Show"}</button>
      </div>
      {!p.has_password && <p className="text-sm text-foreground/70">You signed in with Google, so you don't have a password yet. Set one if you'd also like to sign in with your e-mail.</p>}
      <div className="grid sm:grid-cols-3 gap-4">
        {p.has_password && <Row label="Current password" htmlFor="pw-cur"><input id="pw-cur" type={type} autoComplete="current-password" className={field} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="pw-current" required /></Row>}
        <Row label="New password" htmlFor="pw-new" hint={`At least ${min} characters.`}><input id="pw-new" type={type} autoComplete="new-password" className={field} value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new" required /></Row>
        <Row label="Repeat new password" htmlFor="pw-again"><input id="pw-again" type={type} autoComplete="new-password" className={field} value={again} onChange={(e) => setAgain(e.target.value)} data-testid="pw-again" required /></Row>
      </div>
      {err && <p className="text-sm text-red-700" role="alert" data-testid="pw-error">{err}</p>}
      {p.demo && <p className="text-sm text-amber-800 bg-gold-500/10 rounded-xl px-3 py-2">This is a shared demo account, so its password can't be changed.</p>}
      <button type="submit" className="btn-primary disabled:opacity-50" disabled={busy || p.demo || !next || !again || (p.has_password && !cur)} data-testid="pw-save">{busy ? "Changing…" : p.has_password ? "Change password" : "Set password"}</button>
    </form>
  );
}

function DeleteAccount({ token, p }) {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await accountDelete(token, { confirm: word, ...(p.has_password ? { password: pw } : {}) });
      setDone(r);
      setTimeout(() => window.location.assign("/?switch=1"), 6000);
    } catch (e2) { setErr(errorMessage(e2, "We couldn't delete your account")); }
    finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className={`${card} space-y-2`} role="status" data-testid="delete-done">
        <h2 className="font-display font-semibold text-lg text-forest-700">Your account was deleted</h2>
        <p className="text-sm text-foreground/80">Your sign-in, plan and profile are gone, and so is your data in the apps that could remove it.</p>
        {done.kept_in?.length > 0 && <p className="text-sm text-foreground/80">These apps keep your progress until they add deletion; nobody can sign in to it: <strong>{done.kept_in.join(", ")}</strong>. Write to HikmahVerses support to have it erased.</p>}
        <p className="text-xs text-foreground/60">Taking you to the sign-in page&hellip;</p>
      </div>
    );
  }
  return (
    <section className={`${card} space-y-3 border-red-200`} data-testid="profile-delete">
      <h2 className="font-display font-semibold text-lg text-red-800">Delete my account</h2>
      {!open ? (
        <>
          <p className="text-sm text-foreground/80">Permanently remove your HikmahVerses account, your plan and your progress. This cannot be undone.</p>
          <button type="button" className="btn-ghost text-red-800 border-red-300" onClick={() => setOpen(true)} data-testid="delete-open">Delete my account&hellip;</button>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <ul className="text-sm text-foreground/80 list-disc pl-5 space-y-1">
            <li>Your sign-in, profile and progress are removed from the apps that can delete them (Hifz with Hikmah and 85% Quranic Words).</li>
            <li>Your subscription ends and is <strong>not refunded</strong>. Payment records are kept for accounting.</li>
            <li>Other apps keep their copy of your progress until they add deletion. You will be told which.</li>
            <li>School and family accounts are managed by the school or the family owner, so they can't delete themselves here.</li>
          </ul>
          {p.has_password && <Row label="Your password" htmlFor="del-pw"><input id="del-pw" type="password" autoComplete="current-password" className={field} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="delete-password" required /></Row>}
          <Row label="Type DELETE to confirm" htmlFor="del-word"><input id="del-word" className={field} value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" data-testid="delete-word" required /></Row>
          {err && <p className="text-sm text-red-700" role="alert" data-testid="delete-error">{err}</p>}
          <div className="flex gap-2">
            <button type="submit" className="btn-primary bg-red-700 hover:bg-red-800 disabled:opacity-50" disabled={busy || p.demo || word !== "DELETE" || (p.has_password && !pw)} data-testid="delete-confirm">{busy ? "Deleting\u2026" : "Delete my account for ever"}</button>
            <button type="button" className="btn-ghost" onClick={() => { setOpen(false); setErr(""); }}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}

export default function MyProfile({ token, onSaved }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => accountProfile(token).then((d) => { setP(d); setErr(""); }).catch((e) => setErr(errorMessage(e, "We couldn't load your profile"))), [token]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div className={`${card} text-center`} role="alert" data-testid="profile-error">{err} <button type="button" className="underline" onClick={load}>Try again</button></div>;
  if (!p) return <div className="text-forest-700" data-testid="profile-loading">Loading…</div>;
  const u = p.user;
  const role = u.is_admin ? `${ROLE[u.account_kind] || "Member"} · Admin` : ROLE[u.account_kind] || "Member";
  return (
    <div className="space-y-5 max-w-3xl" data-testid="my-profile">
      <div>
        <h1 className="font-display font-semibold text-2xl text-forest-700">My profile</h1>
        <p className="text-sm text-foreground/70 mt-1">See your details, update them, or change your password.</p>
      </div>
      <div className={`${card} flex flex-wrap items-center gap-4`} data-testid="profile-header">
        <span className="h-16 w-16 rounded-full bg-forest-700 text-sand-100 grid place-items-center shrink-0" aria-hidden><UserRound className="h-8 w-8" /></span>
        <div className="min-w-0">
          <div className="font-display font-semibold text-xl text-forest-700 truncate" data-testid="profile-name">{u.name}</div>
          <div className="text-sm text-foreground/70" data-testid="profile-role">{role}{p.school?.name ? ` · ${p.school.name}` : ""}</div>
          {(p.class_name || p.classes.length > 0) && <div className="text-sm text-foreground/70" data-testid="profile-class">{p.class_name ? `${p.class_name} ${p.section || ""}` : `Classes: ${p.classes.map((c) => `${c.class_name} ${c.section}`).join(", ")}`}</div>}
        </div>
        <div className="ml-auto text-xs text-foreground/60 sm:text-right"><div>Member since {when(u.created_at)}</div><div>Last sign-in {when(u.last_active_at)}</div></div>
      </div>
      <Details token={token} p={p} onSaved={() => { load(); onSaved?.(); }} />
      <Password token={token} p={p} />
      <DeleteAccount token={token} p={p} />
      <p className="text-xs text-foreground/60 inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5" aria-hidden />Only you can see this page. Changing your password keeps you signed in on this device.</p>
    </div>
  );
}
